/**
 * EN-1883: request and state helpers shared by UserService (company admin,
 * /user/...), AdminService (super admin, /admin/user/...) and AdminStore.
 * Pure so they can be unit tested; e2e/user-mgmt-api.unit.spec.ts covers
 * them.
 */
import { UserMgmt } from '../models/UserMgmt';
import { ActiveFor } from './UserExpiry';

export type UserActionPrefix = '/user' | '/admin/user';
export type UserAction = 'deactivate' | 'reactivate' | 'extend';

/** Create/edit (POST /user, POST /admin/user) with an optional period. */
export interface UserUpsertRequest {
  user: UserMgmt;
  activeFor?: ActiveFor;
}

/** POST path of a user action; the Auth0 userId is URL-encoded. */
export function userActionPath(
  prefix: UserActionPrefix,
  userId: string,
  action: UserAction
): string {
  return `${prefix}/${encodeURIComponent(userId)}/${action}`;
}

/** Reactivate body: only sent when a period was picked. */
export function reactivateBody(
  activeFor?: ActiveFor
): { activeFor: ActiveFor } | undefined {
  return activeFor ? { activeFor } : undefined;
}

/**
 * The create/edit body: the user's editable fields, plus activeFor only when
 * the admin picked one. The server-computed expiry fields are never sent.
 */
export function userUpsertPayload(
  user: UserMgmt,
  activeFor?: ActiveFor
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    id: user.id,
    group: user.group,
    name: user.name,
    email: user.email,
    phoneE164: user.phoneE164,
    role: user.role,
    mustChangePassword: user.mustChangePassword,
    isoRegionCode: user.isoRegionCode,
  };
  if (activeFor) payload.activeFor = activeFor;
  return payload;
}

/** Accepts the old `dispatch('upsert…User', user)` form too. */
export function toUpsertRequest(
  payload: UserMgmt | UserUpsertRequest
): UserUpsertRequest {
  if (payload instanceof UserMgmt) return { user: payload };
  const request: UserUpsertRequest = { user: payload.user };
  if (payload.activeFor) request.activeFor = payload.activeFor;
  return request;
}

/**
 * The list with `updated` in place of the user with the same id (same
 * position), or appended when it is not in the list. Never mutates `users`.
 */
export function replaceUser(
  users: readonly UserMgmt[] | null | undefined,
  updated: UserMgmt
): UserMgmt[] {
  const list = users ? [...users] : [];
  const index = list.findIndex((u) => u.id === updated.id);
  if (index === -1) list.push(updated);
  else list[index] = updated;
  return list;
}

/**
 * Super admin: replaceUser, but only when `updated` belongs to the group
 * whose users are shown (`groupId`). A reactivated duplicate from another
 * group is not added to this group's list (review N4). Without a groupId,
 * or when the user has no group, it is replaceUser.
 */
export function replaceUserInGroup(
  users: readonly UserMgmt[] | null | undefined,
  updated: UserMgmt,
  groupId?: number | null
): UserMgmt[] {
  if (groupId != null && updated.group != null && updated.group !== groupId)
    return users ? [...users] : [];
  return replaceUser(users, updated);
}

/**
 * The info dialog's user after the list was re-fetched: the fresh copy
 * with the same id, or `selected` as it was when the user is gone.
 */
export function refreshedUser<T extends { id?: string | null }>(
  users: readonly T[] | null | undefined,
  selected: T | null
): T | null {
  if (!selected || !users) return selected;
  return users.find((u) => u.id === selected.id) || selected;
}
